import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable, { applyPlugin } from "jspdf-autotable";
import { useCompany } from "../../context/CompanyContext";
import { showToast } from "../../utils/customToast";
import {
  FaClock,
  FaArrowDown,
  FaArrowUp,
  FaChartBar,
  FaSearch,
  FaFileExcel,
  FaFilePdf,
  FaPrint,
  FaBalanceScale,
  FaChevronDown,
  FaChevronRight,
} from "react-icons/fa";
import "./BalanceSheet.css";
import { FinancialStatementSkeleton } from "../../Component/Skeleton";

try {
  applyPlugin(jsPDF);
} catch (e) {
  // autoTable already applied or fallback
}

const round2 = (val) => Math.round((Number(val) || 0) * 100) / 100;

// Indian Rupee currency formatter
const formatINR = (val) => {
  const num = Number(val || 0);
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDisplayDate = (dStr) => {
  if (!dStr) return "-";
  const [y, m, d] = String(dStr).split("-");
  if (!y || !m || !d) return dStr;
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];
  const mIndex = parseInt(m, 10) - 1;
  return `${d}-${months[mIndex] || m}-${y}`;
};

export default function BalanceSheet() {
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const BASE_API_URL =
    process.env.REACT_APP_BASE_API_URL ||
    process.env.REACT_APP_API_URL ||
    "http://localhost:5000/api";

  const companyId = useMemo(() => {
    return (
      selectedCompany?.CompID ||
      selectedCompany?.id ||
      sessionStorage.getItem("CompID") ||
      sessionStorage.getItem("companyId") ||
      "1147"
    );
  }, [selectedCompany]);

  const companyInfo = useMemo(() => {
    return {
      name:
        selectedCompany?.Name ||
        selectedCompany?.name ||
        selectedCompany?.CompanyName ||
        "DAZY TOURE TRAVEL'S",
      address:
        selectedCompany?.Address ||
        selectedCompany?.address ||
        "Opp. GPO, M.I. Road, Jaipur-302001",
      gstin: selectedCompany?.GSTNO || selectedCompany?.gstin || "",
      stateCode: selectedCompany?.StateCode || selectedCompany?.stateCode || "08",
    };
  }, [selectedCompany]);

  // Default dates: FY start to today
  const defaultDates = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    const fyStart = m >= 4 ? y : y - 1;
    const dStr = String(now.getDate()).padStart(2, "0");
    const mStr = String(now.getMonth() + 1).padStart(2, "0");
    return {
      fromDate: `${fyStart}-04-01`,
      toDate: `${y}-${mStr}-${dStr}`,
    };
  }, []);

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const urlFromDate = searchParams.get("fromDate");
  const urlToDate = searchParams.get("toDate") || searchParams.get("asOnDate");

  const savedDates = useMemo(() => {
    try {
      const savedFrom = sessionStorage.getItem("bs_from_date");
      const savedTo = sessionStorage.getItem("bs_to_date") || sessionStorage.getItem("bs_as_on_date");
      return {
        fromDate: urlFromDate || savedFrom || defaultDates.fromDate,
        toDate: urlToDate || savedTo || defaultDates.toDate,
      };
    } catch (e) {
      return {
        fromDate: urlFromDate || defaultDates.fromDate,
        toDate: urlToDate || defaultDates.toDate,
      };
    }
  }, [defaultDates, urlFromDate, urlToDate]);

  const [fromDate, setFromDate] = useState(savedDates.fromDate);
  const [toDate, setToDate] = useState(savedDates.toDate);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    sessionStorage.setItem("bs_from_date", fromDate);
    sessionStorage.setItem("bs_to_date", toDate);
    sessionStorage.setItem("bs_as_on_date", toDate);
  }, [fromDate, toDate]);

  // Live timestamp for badge
  const [liveTimestamp, setLiveTimestamp] = useState("");
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const d = String(now.getDate()).padStart(2, "0");
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const m = months[now.getMonth()];
      const y = now.getFullYear();
      let hours = now.getHours();
      const mins = String(now.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      setLiveTimestamp(`${d}-${m}-${y} ${hours}:${mins} ${ampm}`);
    };
    updateTime();
    const interval = setInterval(updateTime, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch Balance Sheet data from API using React Query
  const {
    data: apiData,
    isLoading: loading,
    refetch: fetchBalanceSheet,
  } = useQuery({
    queryKey: ["balanceSheet", companyId, fromDate, toDate],
    queryFn: async () => {
      if (!companyId) return null;
      try {
        const res = await axios.get(`${BASE_API_URL}/reports/balance-sheet/${companyId}`, {
          params: { fromDate, toDate, asOnDate: toDate },
        });
        if (res.data?.success && res.data.data) {
          return res.data.data;
        }
        return null;
      } catch (err) {
        console.error("Failed to fetch Balance Sheet:", err);
        showToast.error("Failed to load Balance Sheet data");
        throw err;
      }
    },
    enabled: Boolean(companyId),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const [formatMode, setFormatMode] = useState("short");

  const handleApplyFilter = () => {
    fetchBalanceSheet();
  };

  const handleReset = () => {
    setFromDate(defaultDates.fromDate);
    setToDate(defaultDates.toDate);
    setSearchTerm("");
    setFormatMode("short");
    setExpandedGroups({});
  };

  const handleGroupClick = (groupCode, groupName) => {
    if (!groupCode) return;
    navigate(
      `/group-ledger-summary?groupCode=${groupCode}&groupName=${encodeURIComponent(
        groupName || ""
      )}&asOnDate=${toDate}&fromDate=${fromDate}`
    );
  };

  const handleLedgerClick = (headCode, headName) => {
    if (!headCode) return;
    navigate(
      `/monthly-ledger-summary?headCode=${headCode}&headName=${encodeURIComponent(
        headName || ""
      )}&asOnDate=${toDate}&fromDate=${fromDate}`,
      {
        state: { accountId: String(headCode), headName },
      }
    );
  };

  const handleFormatChange = (mode) => {
    setFormatMode(mode);
    if (mode === "detail") {
      const allKeys = {};
      const allGroups = [
        ...(apiData?.liabilities?.groups || []),
        ...(apiData?.assets?.groups || []),
      ];
      allGroups.forEach((g, gIdx) => {
        allKeys[`lib-g-${g.groupCode || gIdx}`] = true;
        allKeys[`ast-g-${g.groupCode || gIdx}`] = true;
        (g.subGroups || []).forEach((sg, sgIdx) => {
          allKeys[`lib-g-${g.groupCode || gIdx}-sg-${sg.groupCode || sgIdx}`] = true;
          allKeys[`ast-g-${g.groupCode || gIdx}-sg-${sg.groupCode || sgIdx}`] = true;
        });
      });
      setExpandedGroups(allKeys);
    } else {
      setExpandedGroups({});
    }
  };

  const toggleExpand = (key, e) => {
    e.stopPropagation();
    setExpandedGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Filter groups according to search term
  const filterGroups = (groups) => {
    if (!groups) return [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return groups;

    return groups
      .map((g) => {
        const groupMatch = g.groupName && g.groupName.toLowerCase().includes(term);
        const matchingLedgers = (g.ledgers || []).filter((l) =>
          l.headName && l.headName.toLowerCase().includes(term)
        );
        const matchingSubGroups = (g.subGroups || [])
          .map((sg) => {
            const sgMatch = sg.groupName && sg.groupName.toLowerCase().includes(term);
            const sgLedgers = (sg.ledgers || []).filter((l) =>
              l.headName && l.headName.toLowerCase().includes(term)
            );
            if (sgMatch || sgLedgers.length > 0) {
              return { ...sg, ledgers: sgMatch ? sg.ledgers : sgLedgers };
            }
            return null;
          })
          .filter(Boolean);

        if (groupMatch || matchingLedgers.length > 0 || matchingSubGroups.length > 0) {
          return {
            ...g,
            ledgers: groupMatch ? g.ledgers : matchingLedgers,
            subGroups: groupMatch ? g.subGroups : matchingSubGroups,
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const filteredLiabilities = useMemo(() => {
    return filterGroups(apiData?.liabilities?.groups || []);
  }, [apiData, searchTerm]);

  const filteredAssets = useMemo(() => {
    return filterGroups(apiData?.assets?.groups || []);
  }, [apiData, searchTerm]);

  const isBalanced = useMemo(() => {
    if (!apiData) return true;
    const diff = Math.abs((apiData.grandTotalLiabilities || 0) - (apiData.grandTotalAssets || 0));
    return diff < 0.01;
  }, [apiData]);

  // Render hierarchical group, subgroup, and ledger rows
  const renderRows = (groups, sidePrefix) => {
    if (!groups || groups.length === 0) {
      return (
        <tr>
          <td colSpan="2" style={{ textAlign: "center", padding: "14px", color: "#94a3b8", fontSize: "11px" }}>
            No records found.
          </td>
        </tr>
      );
    }

    const rows = [];
    groups.forEach((g, gIdx) => {
      const gKey = `${sidePrefix}-g-${g.groupCode || gIdx}`;
      const isExpanded = searchTerm || formatMode === "detail" ? true : Boolean(expandedGroups[gKey]);
      const hasChildren = (g.subGroups && g.subGroups.length > 0) || (g.ledgers && g.ledgers.length > 0);

      // Main Group Row
      rows.push(
        <tr
          key={gKey}
          className="bs_clickable_group_row"
          onClick={() => handleGroupClick(g.groupCode, g.groupName)}
          title={`Click to view ${g.groupName} in Group Ledger Summary`}
        >
          <td className="bs_item_name" style={{ paddingLeft: "10px" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
              {hasChildren && (
                <span
                  onClick={(e) => toggleExpand(gKey, e)}
                  style={{ cursor: "pointer", color: "#064e48", display: "inline-flex", fontSize: "10px" }}
                >
                  {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                </span>
              )}
              <span className="bs_link_text">{g.groupName}</span>
            </span>
          </td>
          <td className="bs_item_amt">
            {formatINR(Math.abs(g.total))}
            {g.balanceType && (
              <span
                style={{
                  fontSize: "10px",
                  marginLeft: "5px",
                  fontWeight: "700",
                  color: g.balanceType === "Dr" ? "#166534" : "#b91c1c",
                }}
              >
                {g.balanceType}
              </span>
            )}
          </td>
        </tr>
      );

      // When expanded: Direct Ledgers FIRST (e.g. BBrown Ltd first), then SubGroups (e.g. Sundry Debtors)!
      if (isExpanded) {
        // 1. Direct Ledgers under Main Group FIRST (Clickable + Darker text)
        (g.ledgers || []).forEach((l, lIdx) => {
          rows.push(
            <tr
              key={`${gKey}-dir-l-${l.headCode || lIdx}`}
              className="bs_clickable_ledger_row"
              onClick={() => handleLedgerClick(l.headCode, l.headName)}
              title={`Click to view ${l.headName} in Monthly Ledger Summary`}
            >
              <td className="bs_item_name bs_subgroup_indent">
                <span className="bs_ledger_link">• {l.headName}</span>
              </td>
              <td className="bs_item_amt" style={{ color: "#0f172a", fontWeight: "600" }}>
                {formatINR(l.closingBalance)}
                {l.balanceType && (
                  <span
                    style={{
                      fontSize: "10px",
                      marginLeft: "5px",
                      fontWeight: "700",
                      color: l.balanceType === "Dr" ? "#166534" : "#b91c1c",
                    }}
                  >
                    {l.balanceType}
                  </span>
                )}
              </td>
            </tr>
          );
        });

        // 2. SubGroups SECOND (e.g. Sundry Debtors after BBrown Ltd)
        (g.subGroups || []).forEach((sg, sgIdx) => {
          const sgKey = `${gKey}-sg-${sg.groupCode || sgIdx}`;
          const isSgExpanded = searchTerm || formatMode === "detail" ? true : Boolean(expandedGroups[sgKey]);
          const hasSgLedgers = sg.ledgers && sg.ledgers.length > 0;

          rows.push(
            <tr
              key={sgKey}
              className="bs_clickable_group_row"
              onClick={() => handleGroupClick(sg.groupCode, sg.groupName)}
              title={`Click to view ${sg.groupName} in Group Ledger Summary`}
            >
              <td className="bs_item_name bs_subgroup_indent">
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  {hasSgLedgers && (
                    <span
                      onClick={(e) => toggleExpand(sgKey, e)}
                      style={{ cursor: "pointer", color: "#168078", display: "inline-flex", fontSize: "9px" }}
                    >
                      {isSgExpanded ? <FaChevronDown /> : <FaChevronRight />}
                    </span>
                  )}
                  <span className="bs_link_text">{sg.groupName}</span>
                </span>
              </td>
              <td className="bs_item_amt" style={{ color: "#0f172a", fontWeight: "600" }}>
                {formatINR(Math.abs(sg.total))}
                {sg.balanceType && (
                  <span
                    style={{
                      fontSize: "10px",
                      marginLeft: "5px",
                      fontWeight: "700",
                      color: sg.balanceType === "Dr" ? "#166534" : "#b91c1c",
                    }}
                  >
                    {sg.balanceType}
                  </span>
                )}
              </td>
            </tr>
          );

          if (isSgExpanded) {
            (sg.ledgers || []).forEach((l, lIdx) => {
              rows.push(
                <tr
                  key={`${sgKey}-l-${l.headCode || lIdx}`}
                  className="bs_clickable_ledger_row"
                  onClick={() => handleLedgerClick(l.headCode, l.headName)}
                  title={`Click to view ${l.headName} in Monthly Ledger Summary`}
                >
                  <td className="bs_item_name bs_ledger_indent">
                    <span className="bs_ledger_link">• {l.headName}</span>
                  </td>
                  <td className="bs_item_amt" style={{ color: "#0f172a", fontWeight: "600" }}>
                    {formatINR(l.closingBalance)}
                    {l.balanceType && (
                      <span
                        style={{
                          fontSize: "10px",
                          marginLeft: "5px",
                          fontWeight: "700",
                          color: l.balanceType === "Dr" ? "#166534" : "#b91c1c",
                        }}
                      >
                        {l.balanceType}
                      </span>
                    )}
                  </td>
                </tr>
              );
            });
          }
        });
      }
    });

    return rows;
  };

  const libRows = useMemo(
    () => renderRows(filteredLiabilities, "lib"),
    [filteredLiabilities, expandedGroups, searchTerm, formatMode]
  );
  const astRows = useMemo(
    () => renderRows(filteredAssets, "ast"),
    [filteredAssets, expandedGroups, searchTerm, formatMode]
  );

  const libSpecialCount =
    (!searchTerm && apiData?.profitLoss?.side === "liabilities" ? 1 : 0) +
    (!searchTerm && apiData?.openingBalanceDiff?.side === "liabilities" ? 1 : 0);
  const astSpecialCount =
    (!searchTerm && apiData?.profitLoss?.side === "assets" ? 1 : 0) +
    (!searchTerm && apiData?.openingBalanceDiff?.side === "assets" ? 1 : 0);

  const totalLibCount = libRows.length + libSpecialCount;
  const totalAstCount = astRows.length + astSpecialCount;

  const libSpacerCount = Math.max(0, totalAstCount - totalLibCount);
  const astSpacerCount = Math.max(0, totalLibCount - totalAstCount);

  // =========================================================================
  // EXPORT TO EXCEL
  // =========================================================================
  const handleExportExcel = () => {
    try {
      if (!apiData) return;
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compDetails = `Address: ${companyInfo.address || ""}  |  GSTIN: ${companyInfo.gstin || ""}  |  State: ${companyInfo.stateCode || "08"}`;
      const reportTitle = "BALANCE SHEET";
      const periodSubtitle = `From Date: ${formatDisplayDate(fromDate)}  To Date: ${formatDisplayDate(toDate)}  |  Generated on: ${liveTimestamp}`;

      const headers = ["Liabilities", "Amount (INR)", "", "Assets", "Amount (INR)"];

      // Flatten groups for excel
      const flattenForExcel = (groups) => {
        const items = [];
        (groups || []).forEach((g) => {
          items.push({ name: g.groupName, amt: Math.abs(g.total) });
          (g.subGroups || []).forEach((sg) => {
            items.push({ name: `  ${sg.groupName}`, amt: Math.abs(sg.total) });
            (sg.ledgers || []).forEach((l) => {
              items.push({ name: `    ${l.headName}`, amt: l.closingBalance });
            });
          });
          (g.ledgers || []).forEach((l) => {
            items.push({ name: `  ${l.headName}`, amt: l.closingBalance });
          });
        });
        return items;
      };

      const libItems = flattenForExcel(apiData.liabilities?.groups);
      const astItems = flattenForExcel(apiData.assets?.groups);

      // Add Net Profit/Loss
      if (apiData.profitLoss) {
        if (apiData.profitLoss.side === "liabilities") {
          libItems.push({
            name: apiData.profitLoss.isNetProfit ? "Net Profit" : "Net Loss",
            amt: apiData.profitLoss.isNetProfit ? apiData.profitLoss.netProfit : apiData.profitLoss.netLoss,
          });
        } else if (apiData.profitLoss.side === "assets") {
          astItems.push({
            name: apiData.profitLoss.isNetProfit ? "Net Profit" : "Net Loss",
            amt: apiData.profitLoss.isNetProfit ? apiData.profitLoss.netProfit : apiData.profitLoss.netLoss,
          });
        }
      }

      // Add Opening Balance Difference
      if (apiData.openingBalanceDiff && apiData.openingBalanceDiff.amount > 0.001) {
        if (apiData.openingBalanceDiff.side === "liabilities") {
          libItems.push({
            name: "Opening Balance Difference",
            amt: apiData.openingBalanceDiff.amount,
          });
        } else if (apiData.openingBalanceDiff.side === "assets") {
          astItems.push({
            name: "Opening Balance Difference",
            amt: apiData.openingBalanceDiff.amount,
          });
        }
      }

      const maxLen = Math.max(libItems.length, astItems.length);
      const rows = [];
      for (let i = 0; i < maxLen; i++) {
        const lib = libItems[i] || { name: "", amt: "" };
        const ast = astItems[i] || { name: "", amt: "" };
        rows.push([
          lib.name,
          lib.amt !== "" ? Number(lib.amt) : "",
          "",
          ast.name,
          ast.amt !== "" ? Number(ast.amt) : "",
        ]);
      }

      const totalRow = [
        "Total",
        apiData.grandTotalLiabilities || 0,
        "",
        "Total",
        apiData.grandTotalAssets || 0,
      ];

      const wsData = [
        [activeCompName],
        [compDetails],
        [reportTitle],
        [periodSubtitle],
        [],
        headers,
        ...rows,
        totalRow,
        [],
        ["Note: Figures are in Indian Rupees (INR)."],
        ["etour - Design & Develop by Response Infoway"],
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(wsData);
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } },
        { s: { r: 3, c: 0 }, e: { r: 3, c: 4 } },
      ];
      worksheet["!cols"] = [
        { wch: 38 },
        { wch: 18 },
        { wch: 4 },
        { wch: 38 },
        { wch: 18 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Balance_Sheet");
      XLSX.writeFile(workbook, `Balance_Sheet_${fromDate}_to_${toDate}.xlsx`);
      showToast.success("Excel exported successfully");
    } catch (err) {
      console.error("Failed to export Excel:", err);
      showToast.error("Failed to export Excel");
    }
  };

  // =========================================================================
  // EXPORT TO PDF
  // =========================================================================
  const handleExportPDF = () => {
    try {
      if (!apiData) return;
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compLine2 = [
        companyInfo.address,
        companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "",
        companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : "",
      ]
        .filter(Boolean)
        .join("   |   ");
      const reportTitle = "BALANCE SHEET";

      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.setTextColor(0, 0, 0);
      doc.text(activeCompName, pageWidth / 2, 14, { align: "center" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(40, 40, 40);
      doc.text(compLine2, pageWidth / 2, 19, { align: "center" });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      doc.setTextColor(0, 0, 0);
      doc.text(reportTitle, pageWidth / 2, 26, { align: "center" });
      const titleWidth = doc.getTextWidth(reportTitle);
      doc.setLineWidth(0.75);
      doc.setDrawColor(0, 0, 0);
      doc.line(pageWidth / 2 - titleWidth / 2, 27.5, pageWidth / 2 + titleWidth / 2, 27.5);

      doc.setLineWidth(1);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 31, pageWidth - 14, 31);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      doc.text(`From: ${formatDisplayDate(fromDate)}   To: ${formatDisplayDate(toDate)}`, 14, 36);
      doc.text(`Generated on: ${liveTimestamp}`, pageWidth - 14, 36, { align: "right" });

      doc.setLineWidth(0.5);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 39, pageWidth - 14, 39);

      // Build PDF table rows
      const libGroups = apiData.liabilities?.groups || [];
      const astGroups = apiData.assets?.groups || [];

      const libPdf = libGroups.map((g) => ({ name: g.groupName, amt: formatINR(Math.abs(g.total)) }));
      const astPdf = astGroups.map((g) => ({ name: g.groupName, amt: formatINR(Math.abs(g.total)) }));

      if (apiData.profitLoss) {
        const text = apiData.profitLoss.isNetProfit ? "Net Profit" : "Net Loss";
        const amt = formatINR(
          apiData.profitLoss.isNetProfit ? apiData.profitLoss.netProfit : apiData.profitLoss.netLoss
        );
        if (apiData.profitLoss.side === "liabilities") {
          libPdf.push({ name: text, amt });
        } else if (apiData.profitLoss.side === "assets") {
          astPdf.push({ name: text, amt });
        }
      }

      if (apiData.openingBalanceDiff && apiData.openingBalanceDiff.amount > 0.001) {
        const diffAmt = formatINR(apiData.openingBalanceDiff.amount);
        if (apiData.openingBalanceDiff.side === "liabilities") {
          libPdf.push({ name: "Opening Balance Difference", amt: diffAmt });
        } else if (apiData.openingBalanceDiff.side === "assets") {
          astPdf.push({ name: "Opening Balance Difference", amt: diffAmt });
        }
      }

      const maxLen = Math.max(libPdf.length, astPdf.length);
      const tableRows = [];
      for (let i = 0; i < maxLen; i++) {
        const lib = libPdf[i] || { name: "", amt: "" };
        const ast = astPdf[i] || { name: "", amt: "" };
        tableRows.push([lib.name, lib.amt, ast.name, ast.amt]);
      }

      tableRows.push([
        "Total",
        formatINR(apiData.grandTotalLiabilities),
        "Total",
        formatINR(apiData.grandTotalAssets),
      ]);

      autoTable(doc, {
        head: [
          [
            { content: "Liabilities", styles: { halign: "left" } },
            { content: "Amount (INR)", styles: { halign: "right" } },
            { content: "Assets", styles: { halign: "left" } },
            { content: "Amount (INR)", styles: { halign: "right" } },
          ],
        ],
        body: tableRows,
        startY: 42,
        theme: "striped",
        headStyles: {
          fillColor: [6, 78, 72],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8.5,
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [30, 41, 59],
          cellPadding: 2,
        },
        columnStyles: {
          0: { cellWidth: 62, halign: "left" },
          1: { cellWidth: 30, halign: "right" },
          2: { cellWidth: 62, halign: "left" },
          3: { cellWidth: 30, halign: "right" },
        },
        didParseCell: (data) => {
          const rawRow = data.row.raw || [];
          const firstCol = String(rawRow[0] || "");
          const thirdCol = String(rawRow[2] || "");

          if (firstCol === "Total") {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [248, 250, 252];
            data.cell.styles.textColor = [15, 23, 42];
          }
          if (firstCol === "Net Profit" || thirdCol === "Net Profit") {
            data.cell.styles.textColor = [22, 101, 52];
            data.cell.styles.fontStyle = "bold";
          }
          if (firstCol === "Net Loss" || thirdCol === "Net Loss") {
            data.cell.styles.textColor = [153, 27, 27];
            data.cell.styles.fontStyle = "bold";
          }
          if (firstCol.includes("Opening Balance Difference") || thirdCol.includes("Opening Balance Difference")) {
            data.cell.styles.textColor = [180, 83, 9];
            data.cell.styles.fontStyle = "italic";
          }
        },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(120, 120, 120);
          doc.text("etour - Design & Develop by Response Infoway", 14, doc.internal.pageSize.getHeight() - 8);
          doc.text(`Page ${data.pageNumber} of ${pageCount}`, pageWidth - 14, doc.internal.pageSize.getHeight() - 8, {
            align: "right",
          });
        },
        margin: { left: 14, right: 14, bottom: 15 },
      });

      doc.save(`Balance_Sheet_${fromDate}_to_${toDate}.pdf`);
      showToast.success("PDF exported successfully");
    } catch (err) {
      console.error("Failed to export PDF:", err);
      showToast.error("Failed to export PDF");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="bs_page_wrapper">
      {/* Top Header & Breadcrumb */}
      <div className="bs_header_section">
        <div className="bs_breadcrumb_bar">
          Reports <span>&gt;</span> <strong>Balance Sheet</strong>
        </div>
        <div className="bs_as_on_badge">
          <FaClock />
          As on: {liveTimestamp}
        </div>
      </div>

      {/* Filter Card: From Date & To Date */}
      <div className="bs_filter_card">
        <div className="bs_filter_bar">
          <div className="bs_filter_group">
            <label className="bs_filter_label">
              From Date<span className="required_star">*</span>:
            </label>
            <input
              type="date"
              className="bs_filter_input"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{ width: 135 }}
            />
          </div>

          <div className="bs_filter_group">
            <label className="bs_filter_label">
              To Date<span className="required_star">*</span>:
            </label>
            <input
              type="date"
              className="bs_filter_input"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={{ width: 135 }}
            />
          </div>

          {/* Format Toggle (Short / Detail) - Just after dates */}
          <div className="d-flex align-items-center gap-1" style={{ marginLeft: "12px" }}>
            <span style={{ fontSize: "11px", fontWeight: "600", color: "#4a5568" }}>Format:</span>
            <div className="bs_format_toggle">
              <button
                type="button"
                className={`bs_format_btn ${formatMode === "short" ? "active" : ""}`}
                onClick={() => handleFormatChange("short")}
                title="Condensed / Short View (Collapses All)"
              >
                Short
              </button>
              <button
                type="button"
                className={`bs_format_btn ${formatMode === "detail" ? "active" : ""}`}
                onClick={() => handleFormatChange("detail")}
                title="Detailed View (Expands All Groups & Ledgers)"
              >
                Detail
              </button>
            </div>
          </div>

          {/* Right-most View and Reset buttons */}
          <div className="bs_filter_buttons" style={{ marginLeft: "auto" }}>
            <button
              type="button"
              className="bs_btn_apply"
              onClick={handleApplyFilter}
              disabled={loading}
              title="View Balance Sheet"
            >
              View
            </button>
            <button
              type="button"
              className="bs_btn_reset"
              onClick={handleReset}
              title="Reset filters"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="bs_kpi_row">
        {/* Card 1: Total Liabilities */}
        <div className="bs_kpi_card">
          <div className="bs_kpi_icon_wrapper bs_kpi_icon_liabilities">
            <FaArrowDown />
          </div>
          <div className="bs_kpi_content">
            <span className="bs_kpi_title">Total Liabilities</span>
            <span className="bs_kpi_value">
              ₹ {formatINR(apiData?.grandTotalLiabilities || 0)}
            </span>
          </div>
        </div>

        {/* Card 2: Total Assets */}
        <div className="bs_kpi_card">
          <div className="bs_kpi_icon_wrapper bs_kpi_icon_assets">
            <FaArrowUp />
          </div>
          <div className="bs_kpi_content">
            <span className="bs_kpi_title">Total Assets</span>
            <span className="bs_kpi_value">
              ₹ {formatINR(apiData?.grandTotalAssets || 0)}
            </span>
          </div>
        </div>

        {/* Card 3: Net Profit / Loss */}
        <div className="bs_kpi_card">
          <div className="bs_kpi_icon_wrapper bs_kpi_icon_profit">
            <FaChartBar />
          </div>
          <div className="bs_kpi_content">
            <span className="bs_kpi_title">
              {apiData?.profitLoss?.isNetProfit ? "Net Profit" : "Net Loss"}
            </span>
            <span
              className={`bs_kpi_value ${
                apiData?.profitLoss?.isNetProfit ? "bs_kpi_value_profit" : "bs_kpi_value_loss"
              }`}
            >
              ₹ {formatINR(
                apiData?.profitLoss?.isNetProfit
                  ? apiData?.profitLoss?.netProfit
                  : apiData?.profitLoss?.netLoss || 0
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="bs_table_card">
        {/* Table Toolbar */}
        <div className="bs_table_toolbar">
          <h3 className="bs_table_title">
            Balance Sheet ({formatDisplayDate(fromDate)} to {formatDisplayDate(toDate)})
          </h3>

          <div className="bs_toolbar_right">
            {/* Quick Search */}
            <div className="bs_search_box">
              <input
                type="text"
                className="bs_search_input"
                placeholder="Search ledger or group..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FaSearch className="bs_search_icon" />
            </div>

            {/* Action Buttons */}
            <button
              type="button"
              className="bs_export_btn btn_excel"
              onClick={handleExportExcel}
              title="Export to Microsoft Excel"
            >
              <FaFileExcel />
              Export Excel
            </button>
            <button
              type="button"
              className="bs_export_btn btn_pdf"
              onClick={handleExportPDF}
              title="Export to PDF document"
            >
              <FaFilePdf />
              Export PDF
            </button>
            <button
              type="button"
              className="bs_export_btn btn_print"
              onClick={handlePrint}
              title="Print Statement"
            >
              <FaPrint />
              Print
            </button>
          </div>
        </div>

        {loading ? (
          <FinancialStatementSkeleton title="Balance Sheet" />
        ) : !apiData ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>
            No data available. Please select a valid date and try again.
          </div>
        ) : (
          <>
            {/* T-Format Two Columns Layout */}
            <div className="bs_statements_container">
              {/* LEFT: Liabilities */}
              <div className="bs_column_card">
                <div className="bs_column_header bs_header_liabilities">
                  <span>Liabilities</span>
                </div>
                <table className="bs_statement_table">
                  <thead>
                    <tr>
                      <th style={{ width: "65%" }}>Particulars</th>
                      <th style={{ width: "35%", textAlign: "right" }}>Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {libRows}

                    {/* Net Profit row on Liabilities side */}
                    {!searchTerm && apiData.profitLoss?.side === "liabilities" && (
                      <tr className="bs_net_profit_row">
                        <td className="bs_item_name" style={{ paddingLeft: "10px" }}>
                          ★ Net Profit (from P&L)
                        </td>
                        <td className="bs_item_amt" style={{ color: "#166534" }}>
                          {formatINR(apiData.profitLoss.netProfit)}
                        </td>
                      </tr>
                    )}

                    {/* Opening Balance Difference on Liabilities side */}
                    {!searchTerm && apiData.openingBalanceDiff?.side === "liabilities" && (
                      <tr className="bs_op_diff_row">
                        <td className="bs_item_name" style={{ paddingLeft: "10px" }}>
                          Opening Balance Difference
                        </td>
                        <td className="bs_item_amt" style={{ color: "#b45309" }}>
                          {formatINR(apiData.openingBalanceDiff.amount)}
                        </td>
                      </tr>
                    )}

                    {/* Spacer rows to balance height with Assets table */}
                    {Array.from({ length: libSpacerCount }).map((_, idx) => (
                      <tr key={`lib-sp-${idx}`} className="bs_spacer_row">
                        <td className="bs_item_name">&nbsp;</td>
                        <td className="bs_item_amt">&nbsp;</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bs_column_total_row">
                      <td>Total Liabilities</td>
                      <td style={{ textAlign: "right" }}>
                        ₹ {formatINR(apiData.grandTotalLiabilities)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* RIGHT: Assets */}
              <div className="bs_column_card">
                <div className="bs_column_header bs_header_assets">
                  <span>Assets</span>
                </div>
                <table className="bs_statement_table">
                  <thead>
                    <tr>
                      <th style={{ width: "65%" }}>Particulars</th>
                      <th style={{ width: "35%", textAlign: "right" }}>Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {astRows}

                    {/* Net Loss row on Assets side */}
                    {!searchTerm && apiData.profitLoss?.side === "assets" && (
                      <tr className="bs_net_loss_row">
                        <td className="bs_item_name" style={{ paddingLeft: "10px" }}>
                          ★ Net Loss (from P&L)
                        </td>
                        <td className="bs_item_amt" style={{ color: "#991b1b" }}>
                          {formatINR(apiData.profitLoss.netLoss)}
                        </td>
                      </tr>
                    )}

                    {/* Opening Balance Difference on Assets side */}
                    {!searchTerm && apiData.openingBalanceDiff?.side === "assets" && (
                      <tr className="bs_op_diff_row">
                        <td className="bs_item_name" style={{ paddingLeft: "10px" }}>
                          Opening Balance Difference
                        </td>
                        <td className="bs_item_amt" style={{ color: "#b45309" }}>
                          {formatINR(apiData.openingBalanceDiff.amount)}
                        </td>
                      </tr>
                    )}

                    {/* Spacer rows to balance height with Liabilities table */}
                    {Array.from({ length: astSpacerCount }).map((_, idx) => (
                      <tr key={`ast-sp-${idx}`} className="bs_spacer_row">
                        <td className="bs_item_name">&nbsp;</td>
                        <td className="bs_item_amt">&nbsp;</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bs_column_total_row">
                      <td>Total Assets</td>
                      <td style={{ textAlign: "right" }}>
                        ₹ {formatINR(apiData.grandTotalAssets)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

          </>
        )}

      </div>

      {/* DEDICATED PRINT CONTAINER */}
      <div className="bs_print_area d-none">
        {/* Company Header */}
        <div
          style={{
            textAlign: "center",
            borderBottom: "2px solid #000000",
            paddingBottom: "8px",
            marginBottom: "8px",
          }}
        >
          <h2
            style={{
              margin: "0 0 3px 0",
              fontSize: "19px",
              fontWeight: "bold",
              textTransform: "uppercase",
              color: "#000000",
            }}
          >
            {companyInfo.name}
          </h2>
          <div style={{ fontSize: "11px", color: "#222222", lineHeight: 1.4 }}>
            {companyInfo.address && `${companyInfo.address}  |  `}
            <strong>GSTIN:</strong> {companyInfo.gstin}
            {companyInfo.stateCode && `  |  State Code: ${companyInfo.stateCode}`}
          </div>
          <div
            style={{
              marginTop: "6px",
              fontSize: "13px",
              fontWeight: "bold",
              color: "#000000",
              textDecoration: "underline",
            }}
          >
            BALANCE SHEET
          </div>
        </div>

        {/* Print Meta */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "11px",
            marginBottom: "8px",
            borderBottom: "1px solid #000000",
            paddingBottom: "4px",
          }}
        >
          <div>
            <strong>Period:</strong> {formatDisplayDate(fromDate)} to {formatDisplayDate(toDate)}
          </div>
          <div style={{ textAlign: "right" }}>
            <strong>Printed on:</strong> {liveTimestamp}
          </div>
        </div>

        {/* Print Table */}
        <table className="bs_print_table">
          <thead>
            <tr>
              <th style={{ width: "35%" }}>Liabilities</th>
              <th style={{ width: "15%", textAlign: "right" }}>Amount (₹)</th>
              <th style={{ width: "35%" }}>Assets</th>
              <th style={{ width: "15%", textAlign: "right" }}>Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({
              length: Math.max(
                (apiData?.liabilities?.groups || []).length,
                (apiData?.assets?.groups || []).length
              ),
            }).map((_, i) => {
              const lib = apiData?.liabilities?.groups?.[i] || { groupName: "", total: "" };
              const ast = apiData?.assets?.groups?.[i] || { groupName: "", total: "" };
              return (
                <tr key={`print-row-${i}`} className={i % 2 === 1 ? "print_row_alt" : ""}>
                  <td style={{ fontWeight: 600 }}>{lib.groupName}</td>
                  <td style={{ textAlign: "right" }}>{lib.total !== "" ? formatINR(lib.total) : ""}</td>
                  <td style={{ fontWeight: 600 }}>{ast.groupName}</td>
                  <td style={{ textAlign: "right" }}>{ast.total !== "" ? formatINR(ast.total) : ""}</td>
                </tr>
              );
            })}

            {/* P&L Print Row */}
            {apiData?.profitLoss && (
              <tr>
                <td style={{ fontWeight: 700 }}>
                  {apiData.profitLoss.side === "liabilities"
                    ? apiData.profitLoss.isNetProfit
                      ? "Net Profit"
                      : "Net Loss"
                    : ""}
                </td>
                <td style={{ textAlign: "right", fontWeight: 700 }}>
                  {apiData.profitLoss.side === "liabilities"
                    ? formatINR(
                        apiData.profitLoss.isNetProfit
                          ? apiData.profitLoss.netProfit
                          : apiData.profitLoss.netLoss
                      )
                    : ""}
                </td>
                <td style={{ fontWeight: 700 }}>
                  {apiData.profitLoss.side === "assets"
                    ? apiData.profitLoss.isNetProfit
                      ? "Net Profit"
                      : "Net Loss"
                    : ""}
                </td>
                <td style={{ textAlign: "right", fontWeight: 700 }}>
                  {apiData.profitLoss.side === "assets"
                    ? formatINR(
                        apiData.profitLoss.isNetProfit
                          ? apiData.profitLoss.netProfit
                          : apiData.profitLoss.netLoss
                      )
                    : ""}
                </td>
              </tr>
            )}

            {/* Opening Balance Difference Print Row */}
            {apiData?.openingBalanceDiff && apiData.openingBalanceDiff.amount > 0.001 && (
              <tr>
                <td style={{ fontStyle: "italic", fontWeight: 600 }}>
                  {apiData.openingBalanceDiff.side === "liabilities" ? "Opening Balance Difference" : ""}
                </td>
                <td style={{ textAlign: "right", fontStyle: "italic" }}>
                  {apiData.openingBalanceDiff.side === "liabilities"
                    ? formatINR(apiData.openingBalanceDiff.amount)
                    : ""}
                </td>
                <td style={{ fontStyle: "italic", fontWeight: 600 }}>
                  {apiData.openingBalanceDiff.side === "assets" ? "Opening Balance Difference" : ""}
                </td>
                <td style={{ textAlign: "right", fontStyle: "italic" }}>
                  {apiData.openingBalanceDiff.side === "assets"
                    ? formatINR(apiData.openingBalanceDiff.amount)
                    : ""}
                </td>
              </tr>
            )}

            <tr className="print_total_row">
              <td style={{ fontWeight: 800 }}>Total</td>
              <td style={{ textAlign: "right", fontWeight: 800 }}>
                {formatINR(apiData?.grandTotalLiabilities)}
              </td>
              <td style={{ fontWeight: 800 }}>Total</td>
              <td style={{ textAlign: "right", fontWeight: 800 }}>
                {formatINR(apiData?.grandTotalAssets)}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Footer */}
        <div className="bs_print_footer">
          <span>etour - Design & Develop by Response Infoway</span>
          <span>Printed on: {liveTimestamp}</span>
        </div>
      </div>
    </div>
  );
}
